import type { OfferApi } from '@fourthwall/services/dist/OffersApiClient'
import type { UseInfiniteQueryOptions } from '@tanstack/react-query'

import { useOffersInfiniteQuery } from '../../../hooks/useOffersInfiniteQuery'
import type {
  SelectOffersSource,
  UnifiedOffer,
  UnifiedPage,
  UseUnifiedInfiniteQueryResult,
} from '../SelectProductsModal.types'
import { useOffersListingInfiniteQuery } from './useOffersListingInfiniteQuery'

const PAGE_SIZE = 20

type OffersParams = OfferApi.GetOffersParams
type ListingParams = Parameters<typeof useOffersListingInfiniteQuery>[0]

export const useUnifiedInfiniteOffers = (
  source: SelectOffersSource,
  query?: string,
  params?: OffersParams | ListingParams,
  options?: UseInfiniteQueryOptions<any>,
): UseUnifiedInfiniteQueryResult => {
  if (source === 'offers') {
    const { offersInfiniteQuery } = useOffersInfiniteQuery(
      {
        size: PAGE_SIZE,
        q: query && query.length > 2 ? query : undefined,
        ...(params as OffersParams),
      },
      {
        keepPreviousData: true,
        ...(options as UseInfiniteQueryOptions<OfferApi.GetOffersResponse>),
      },
    )

    const pages: UnifiedPage[] | undefined = offersInfiniteQuery.data?.pages.map((p) => ({
      items: p.offers.map<UnifiedOffer>((o) => ({
        id: o.id,
        name: o.name,
        image: o.primaryImage?.thumbnail.url,
        state: o.state.status,
        variants: (o.variants || []).map((v) => ({
          id: v.id,
          name: v.attributesDescription,
          color: v.colorAttribute?.colorSwatch
            ? { label: v.colorAttribute.name, value: v.colorAttribute.colorSwatch }
            : undefined,
        })),
        hasHsCode: Boolean(o.customsInformation?.shortHsCode),
        available: o.state.available,
      })),
      page: p.page,
    }))

    return {
      products: pages?.map((p) => p.items).flat(),
      hasNextPage: offersInfiniteQuery.hasNextPage,
      isLoading: offersInfiniteQuery.isLoading,
      fetchNextPage: offersInfiniteQuery.fetchNextPage,
    }
  }

  const { query: listingQuery } = useOffersListingInfiniteQuery(
    {
      size: PAGE_SIZE,
      q: query && query.length > 2 ? query : undefined,
      ...(params as ListingParams),
    },
    options as UseInfiniteQueryOptions<any>,
  )

  const pages: UnifiedPage[] | undefined = listingQuery.data?.pages.map((p) => ({
    items: p.offers.map<UnifiedOffer>((o) => ({
      id: o.id,
      name: o.name,
      image: o.thumbnail,
      state: o.status,
      variants: [],
      hasHsCode: false,
      available: o.stock.type !== 'SOLD_OUT',
    })),
    page: p.page,
  }))

  return {
    products: pages?.map((p) => p.items).flat(),
    hasNextPage: listingQuery.hasNextPage,
    isLoading: listingQuery.isLoading,
    fetchNextPage: listingQuery.fetchNextPage,
  }
}
